import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Copy, X, Share, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QRCodeModal({ open, onClose, profileUrl, builderName }) {
    const ref = useRef(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const handleDownload = () => {
        const canvas = ref.current?.querySelector("canvas");
        if (!canvas) return;
        const link = document.createElement("a");
        link.download = `${builderName || "builder"}-qr.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    const handleCopy = async () => {
        await navigator.clipboard.writeText(profileUrl);
        setCopied(true);
        toast.success("Link copied to clipboard");
        setTimeout(() => setCopied(false), 1800);
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: builderName, url: profileUrl });
            } catch (e) {
                /* user cancelled */
            }
        } else {
            handleCopy();
        }
    };

    return (
        <div
            data-testid="qr-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={onClose}
        >
            <div
                className="relative bg-white max-w-md w-full p-8 sm:p-10 fade-in-up"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    data-testid="qr-modal-close"
                    className="absolute top-4 right-4 p-2 hover:bg-neutral-100"
                    aria-label="Close"
                >
                    <X size={20} />
                </button>

                <div className="text-overline text-[#E63946] mb-2">Your QR code</div>
                <h2 className="font-display text-3xl font-black tracking-tighter leading-none mb-2">
                    Scan & view portfolio
                </h2>
                <p className="text-sm text-neutral-600 mb-6">
                    Print on flyers, business cards, or van stickers. Customers tap their phone to your work.
                </p>

                <div ref={ref} className="bg-white border-2 border-black p-6 flex items-center justify-center">
                    <QRCodeCanvas
                        value={profileUrl}
                        size={240}
                        bgColor="#ffffff"
                        fgColor="#0a0a0a"
                        level="H"
                        includeMargin={false}
                    />
                </div>

                <div className="mt-4 px-3 py-2 bg-neutral-100 text-xs text-neutral-700 font-mono-accent break-all">
                    {profileUrl}
                </div>

                <div className="grid grid-cols-3 gap-2 mt-5">
                    <button
                        data-testid="qr-download-btn"
                        onClick={handleDownload}
                        className="btn-primary justify-center text-xs"
                    >
                        <Download size={16} weight="bold" /> PNG
                    </button>
                    <button
                        data-testid="qr-copy-btn"
                        onClick={handleCopy}
                        className="btn-ghost justify-center text-xs"
                    >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                        {copied ? "Copied" : "Copy"}
                    </button>
                    <button
                        data-testid="qr-share-btn"
                        onClick={handleShare}
                        className="btn-ghost justify-center text-xs"
                    >
                        <Share size={16} /> Share
                    </button>
                </div>
            </div>
        </div>
    );
}
